/**
 * CivicPulse language toggle — dropdown in the header showing native
 * script for each language. Selection is persisted in localStorage
 * (`civic.i18n.lang`) and broadcast via the `civic-i18n:change` event
 * so every page using `useCivicI18n()` re-renders together.
 */
import { useEffect, useRef, useState } from "react";
import { Globe, Check, Loader2 } from "lucide-react";
import { useCivicI18n } from "@/pages/civic/useCivicI18n";

export default function CivicLanguageToggle() {
  const { lang, setLang, langs, isLoading } = useCivicI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = langs.find((l) => l.code === lang) || langs[0];

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="civic-language-toggle">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Language: ${current.label}`}
        data-testid="civic-language-btn"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-emerald-50 text-slate-700 text-sm font-medium transition"
      >
        {isLoading
          ? <Loader2 className="w-4 h-4 animate-spin text-emerald-700" />
          : <Globe className="w-4 h-4 text-emerald-700" />}
        <span className="hidden sm:inline">{current.native}</span>
        <span className="sm:hidden text-xs uppercase font-bold">{current.code}</span>
      </button>
      {open && (
        <div
          role="menu"
          data-testid="civic-language-menu"
          className="absolute right-0 mt-2 w-56 max-h-[70vh] overflow-y-auto rounded-2xl bg-white ring-1 ring-emerald-100 shadow-xl py-2 z-50"
        >
          <div className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
            Reading in
          </div>
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              data-testid={`civic-language-option-${l.code}`}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition ${
                l.code === lang
                  ? "bg-emerald-50 text-emerald-800 font-semibold"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="font-mono text-[10px] w-6 uppercase text-slate-400">{l.code}</span>
              <span className="flex-1">{l.native}</span>
              <span className="text-[11px] text-slate-400">{l.label}</span>
              {l.code === lang && <Check className="w-4 h-4 text-emerald-700 ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
